// Title screen — cinematic night-arena shot with the broadcast gold signature
// line. Always dark by design. New-game is a 3-step flow: mode → region
// (Satisfactory-style "where do you set up?") → name + difficulty.

import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameStore'
import { useCareerStore } from '@/store/careerStore'
import { useAudioStore } from '@/store/audioStore'
import { DEFAULT_CATEGORY_ID, DEFAULT_MUSIC_TRACK_ID } from '@/config/musicLibrary'
import { useTutorialStore } from '@/store/tutorialStore'
import { startCoachCareer } from '@/game/systems/careerActions'
import { seedOpeningStory } from '@/game/systems/StorySystem'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { GAME_CONFIG } from '@/config/gameConfig'
import {
  REGION_CONFIG, REGION_META, REGION_ORDER, getRegionTraits, type RegionId, type RegionDifficultyHint,
} from '@/config/regionConfig'
import { GAME_MODES, type GameMode } from '@/config/gameModesConfig'
import { motion, AnimatePresence } from 'framer-motion'
import { Play, FolderOpen, Lock, ChevronLeft, ChevronRight } from 'lucide-react'

interface MainMenuProps {
  onStartGame: () => void
}

type Step = 'mode' | 'region' | 'name'

const HINT_TONE: Record<RegionDifficultyHint, string> = {
  'Fácil': 'tone-easy', 'Normal': 'tone-normal', 'Media': 'tone-mid', 'Difícil': 'tone-hard',
}

// Horizontal slide for the region carousel (direction: 1 = next, -1 = prev)
const slideVariants = {
  enter: (dir: number) => ({ x: dir >= 0 ? '55%' : '-55%', opacity: 0, scale: 0.95 }),
  center: { x: '0%', opacity: 1, scale: 1 },
  exit: (dir: number) => ({ x: dir >= 0 ? '-55%' : '55%', opacity: 0, scale: 0.95 }),
}

export function MainMenu({ onStartGame }: MainMenuProps) {
  const hasSave = typeof window !== 'undefined' && localStorage.getItem('padel-manager-save')
  const [step, setStep] = useState<Step>('mode')
  const [mode, setMode] = useState<GameMode>('director')
  const [region, setRegion] = useState<RegionId>('costa_sol')
  const [clubName, setClubName] = useState('')
  const [regionDir, setRegionDir] = useState(0)

  const regionIdx = REGION_ORDER.indexOf(region)
  const paginateRegion = (dir: number) => {
    const n = REGION_ORDER.length
    setRegionDir(dir)
    setRegion(REGION_ORDER[(regionIdx + dir + n) % n])
  }

  // Arrow-key navigation while choosing a region
  useEffect(() => {
    if (step !== 'region') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') paginateRegion(1)
      else if (e.key === 'ArrowLeft') paginateRegion(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, regionIdx])

  const handleNewGame = () => {
    const store = useGameStore.getState()
    const name = clubName.trim() || 'Mi Club'
    const rmods = REGION_CONFIG[region]
    store.resetGame()
    useCareerStore.getState().reset() // a fresh career, not carried from a past run
    useCareerStore.getState().setCurrentClubName(name)
    store.setGameMode(mode)
    store.setRegion(region)
    // Club name + starting money/reputation: region one-shots. Difficulty stays
    // at the neutral 'normal' profile (resetGame default) — the chosen zone is
    // the challenge axis now, so there's no separate difficulty selector.
    useGameStore.setState({
      managerClubName: name,
      money: Math.round(GAME_CONFIG.STARTING_MONEY * rmods.startingMoneyMultiplier),
      reputation: 3 + rmods.startingReputationDelta,
    })
    if (mode === 'coach') {
      // Coach takes over an established club; seed it (also sets the budget)
      startCoachCareer()
    }
    // Seed the opening story (region intro beat + welcome/rival feed posts),
    // then the tutorial cards. Done here (not on GAME_STARTED) so it only fires
    // on a brand-new game, never on "Continuar".
    seedOpeningStory({ region, clubName: name, gameMode: mode, day: 1 })
    // A brand-new game always opens the onboarding tutorial (even for veterans
    // who finished it on a previous run — it's skippable from the card).
    useTutorialStore.getState().startTutorial(mode)
    // Default the soundtrack to the "Motivación" playlist for new games.
    useAudioStore.setState({ currentCategoryId: DEFAULT_CATEGORY_ID, currentTrackId: DEFAULT_MUSIC_TRACK_ID })
    // Always land on the dashboard: the URL hash can be stale from a previous
    // session (HashRouter only mounts while playing), which is why exiting from
    // Ajustes and starting again reopened Ajustes.
    window.location.hash = '#/dashboard'
    store.setPlaying(true)
    EventBus.emit(GameEvents.GAME_STARTED)
    onStartGame()
  }

  const handleContinue = () => {
    useGameStore.getState().setPlaying(true)
    EventBus.emit(GameEvents.GAME_STARTED)
    onStartGame()
  }

  const tagline = step === 'mode'
    ? 'Elige cómo quieres vivir el pádel'
    : step === 'region'
      ? '¿Dónde montas tu club?'
      : 'Ponle nombre a tu club'

  return (
    <div className="main-menu">
      <div className="menu-bg">
        <img src="/assets/campus/hero/club_l7.webp" alt="" aria-hidden className="menu-bg-img" />
      </div>
      <div className="menu-veil" />

      <motion.div
        className="menu-content"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="menu-logo">
          <img src="/assets/logo.webp" alt="Padel Club Manager" className="logo-image" />
        </div>

        <p className="menu-tagline">{tagline}</p>
        <div className="menu-courtline" aria-hidden />

        {/* STEP 1 — game mode */}
        {step === 'mode' && (
          <motion.div key="mode" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ width: '100%' }}>
            <div className="menu-modes">
              {GAME_MODES.map(m => {
                const Icon = m.icon
                const selected = mode === m.id
                return (
                  <button
                    key={m.id}
                    className={`mode-card${selected ? ' selected' : ''}${m.available ? '' : ' locked'}`}
                    onClick={() => m.available && setMode(m.id)}
                    disabled={!m.available}
                    aria-pressed={selected}
                  >
                    {!m.available && (
                      <span className="mode-soon"><Lock size={10} /> Próximamente</span>
                    )}
                    <Icon size={22} className="mode-icon" />
                    <span className="mode-label">{m.label}</span>
                    <span className="mode-tagline">{m.tagline}</span>
                  </button>
                )
              })}
            </div>

            <div className="menu-buttons">
              <button className="btn btn-cta menu-btn-main" onClick={() => setStep('region')}>
                Siguiente <ChevronRight size={18} />
              </button>
              {hasSave && (
                <button className="btn menu-btn-ghost" onClick={handleContinue}>
                  <FolderOpen size={16} /> Continuar
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* STEP 2 — region carousel (the Satisfactory-style choice): one big,
            legible zone at a time; arrows, dots, swipe and ← → all navigate */}
        {step === 'region' && (
          <motion.div key="region" initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} style={{ width: '100%' }}>
            <div className="region-carousel">
              <button className="region-arrow left" onClick={() => paginateRegion(-1)} aria-label="Zona anterior">
                <ChevronLeft size={22} />
              </button>

              <div className="region-stage">
                <AnimatePresence custom={regionDir} initial={false}>
                  <motion.div
                    key={region}
                    className="region-slide"
                    custom={regionDir}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ x: { type: 'spring', stiffness: 320, damping: 34 }, opacity: { duration: 0.18 } }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.18}
                    onDragEnd={(_, info) => {
                      if (info.offset.x < -60) paginateRegion(1)
                      else if (info.offset.x > 60) paginateRegion(-1)
                    }}
                    style={{ ['--rg1' as string]: REGION_META[region].gradient[0], ['--rg2' as string]: REGION_META[region].gradient[1] }}
                  >
                    <span className="region-slide-img" style={{ backgroundImage: `url(${REGION_META[region].heroImage})` }} />
                    <span className={`region-slide-hint ${HINT_TONE[REGION_META[region].difficultyHint]}`}>{REGION_META[region].difficultyHint}</span>
                    <span className="region-slide-body">
                      <span className="region-slide-label">{REGION_META[region].label}</span>
                      <span className="region-slide-tagline">{REGION_META[region].tagline}</span>
                      <span className="region-slide-traits">
                        {getRegionTraits(region).map((t, i) => (
                          <span key={i} className={`region-trait ${t.kind}`}>
                            {t.kind !== 'neutral' && (
                              <span className="region-trait-sign">{t.kind === 'pro' ? '+' : '−'}</span>
                            )}
                            {t.label}
                          </span>
                        ))}
                      </span>
                    </span>
                  </motion.div>
                </AnimatePresence>
              </div>

              <button className="region-arrow right" onClick={() => paginateRegion(1)} aria-label="Zona siguiente">
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="region-dots">
              {REGION_ORDER.map((id, i) => (
                <button
                  key={id}
                  className={`region-dot${id === region ? ' active' : ''}`}
                  onClick={() => { setRegionDir(i >= regionIdx ? 1 : -1); setRegion(id) }}
                  aria-label={REGION_META[id].label}
                  aria-current={id === region}
                />
              ))}
            </div>

            <div className="menu-step-nav">
              <button className="btn menu-btn-ghost menu-btn-back" onClick={() => setStep('mode')}>
                <ChevronLeft size={16} /> Atrás
              </button>
              <button className="btn btn-cta menu-btn-main" onClick={() => setStep('name')}>
                Siguiente <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — name */}
        {step === 'name' && (
          <motion.div key="name" initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="menu-setup">
              <input
                className="menu-clubname"
                type="text"
                value={clubName}
                maxLength={28}
                onChange={e => setClubName(e.target.value)}
                placeholder="Nombre de tu club"
                autoFocus
              />
              <div className="menu-summary">
                {REGION_META[region].label} · {REGION_META[region].difficultyHint} · {GAME_MODES.find(m => m.id === mode)?.label ?? 'Director'}
              </div>
            </div>

            <div className="menu-step-nav">
              <button className="btn menu-btn-ghost menu-btn-back" onClick={() => setStep('region')}>
                <ChevronLeft size={16} /> Atrás
              </button>
              <button className="btn btn-cta menu-btn-main" onClick={handleNewGame}>
                <Play size={18} fill="currentColor" /> Nueva Partida
              </button>
            </div>
          </motion.div>
        )}

        <div className="menu-version">v0.5.0 · Beta</div>
      </motion.div>

      <style>{`
        .main-menu { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 100; background: #0D1520; overflow-y: auto; }
        .menu-bg { position: fixed; inset: 0; overflow: hidden; }
        .menu-bg-img { width: 100%; height: 100%; object-fit: cover; animation: menu-drift 40s ease-in-out infinite alternate; transform-origin: center; }
        @keyframes menu-drift { from { transform: scale(1.02) translateX(-0.5%); } to { transform: scale(1.1) translateX(0.5%); } }
        .menu-veil { position: fixed; inset: 0; background: radial-gradient(ellipse at center 35%, rgba(13, 21, 32, 0.25) 0%, rgba(13, 21, 32, 0.88) 78%), linear-gradient(180deg, rgba(13, 21, 32, 0.45) 0%, rgba(13, 21, 32, 0.92) 100%); }
        .menu-content { position: relative; display: flex; flex-direction: column; align-items: center; padding: 2.2rem 2rem; max-width: 600px; width: 100%; text-align: center; }
        .menu-logo { margin-bottom: 0.8rem; }
        .logo-image { max-width: 220px; width: 100%; height: auto; filter: drop-shadow(0 0 34px rgba(245, 197, 66, 0.4)); }
        .menu-tagline { color: #CBD5E1; font-size: 1rem; font-weight: 500; letter-spacing: 0.02em; text-shadow: 0 1px 4px rgba(0,0,0,0.6); }
        .menu-courtline { width: 210px; height: 3px; margin: 0.8rem 0 1.4rem; border-radius: 2px; background: linear-gradient(90deg, transparent 0, #F5C542 22%, #FFF3C4 50%, #F5C542 58%, transparent 62%, transparent 68%, rgba(245, 197, 66, 0.45) 68%, transparent 100%); box-shadow: 0 0 12px rgba(245, 197, 66, 0.5); }

        .menu-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; width: 100%; margin-bottom: 1.4rem; }
        .mode-card { position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 0.3rem; padding: 0.85rem 0.95rem; text-align: left; background: rgba(13, 21, 32, 0.6); border: 1px solid var(--glass-border); clip-path: var(--angle-clip); cursor: pointer; color: var(--text-primary); backdrop-filter: blur(6px); transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease; }
        .mode-card:hover:not(:disabled):not(.selected) { border-color: var(--glass-border-hover); }
        .mode-card.selected { border-color: color-mix(in srgb, var(--gold) 60%, transparent); background: linear-gradient(135deg, var(--gold-fill), rgba(13, 21, 32, 0.6) 70%); box-shadow: var(--glow-gold); }
        .mode-card.locked { cursor: not-allowed; opacity: 0.55; }
        .mode-icon { color: var(--gold); flex: none; }
        .mode-card.locked .mode-icon { color: var(--text-muted); }
        .mode-label { font-family: var(--font-titles); font-weight: 700; font-size: 0.85rem; color: var(--text-primary); }
        .mode-tagline { font-size: 0.68rem; line-height: 1.3; color: var(--text-muted); }
        .mode-soon { position: absolute; top: 0.5rem; right: 0.6rem; display: inline-flex; align-items: center; gap: 0.2rem; font-size: 0.55rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-secondary); background: var(--fill-soft); padding: 0.1rem 0.35rem; border-radius: 3px; }

        /* Region carousel — one big legible zone at a time */
        .region-carousel { display: flex; align-items: center; gap: 0.5rem; width: 100%; margin-bottom: 0.9rem; }
        .region-stage { position: relative; flex: 1; min-width: 0; height: 340px; overflow: hidden; border-radius: var(--radius-md, 12px); }
        .region-slide { position: absolute; inset: 0; border-radius: var(--radius-md, 12px); overflow: hidden; border: 1px solid var(--glass-border); background: linear-gradient(135deg, var(--rg1), var(--rg2)); cursor: grab; }
        .region-slide:active { cursor: grabbing; }
        .region-slide-img { position: absolute; inset: 0; background-size: cover; background-position: center; pointer-events: none; }
        .region-slide-body { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; gap: 0.35rem; padding: 1rem 1.1rem; text-align: left; background: linear-gradient(180deg, transparent 30%, rgba(13, 21, 32, 0.6) 58%, rgba(13, 21, 32, 0.96) 100%); pointer-events: none; }
        .region-slide-label { font-family: var(--font-titles); font-weight: 800; font-size: 1.5rem; line-height: 1.05; color: #fff; text-shadow: 0 1px 6px rgba(0,0,0,0.7); }
        .region-slide-tagline { font-size: 0.9rem; line-height: 1.4; color: rgba(232, 238, 246, 0.95); text-shadow: 0 1px 4px rgba(0,0,0,0.85); }
        .region-slide-hint { position: absolute; top: 0.8rem; left: 0.8rem; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; padding: 0.22rem 0.6rem; border-radius: 5px; z-index: 2; }
        .region-slide-hint.tone-easy { background: color-mix(in srgb, var(--success) 88%, #000); color: #fff; }
        .region-slide-hint.tone-normal { background: var(--gradient-gold, #F5C542); color: var(--gold-contrast, #0D1520); }
        .region-slide-hint.tone-mid { background: color-mix(in srgb, #38BDF8 85%, #000); color: #fff; }
        .region-slide-hint.tone-hard { background: color-mix(in srgb, var(--danger) 88%, #000); color: #fff; }

        .region-slide-traits { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.2rem; }
        .region-trait { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.78rem; font-weight: 700; line-height: 1; padding: 0.32rem 0.62rem; border-radius: 999px; border: 1px solid transparent; white-space: nowrap; text-shadow: none; }
        .region-trait-sign { font-weight: 900; font-size: 0.9rem; }
        .region-trait.pro { background: color-mix(in srgb, var(--success) 26%, rgba(13, 21, 32, 0.72)); border-color: color-mix(in srgb, var(--success) 58%, transparent); color: #ddf7e6; }
        .region-trait.con { background: color-mix(in srgb, var(--danger) 26%, rgba(13, 21, 32, 0.72)); border-color: color-mix(in srgb, var(--danger) 55%, transparent); color: #fddcdc; }
        .region-trait.neutral { background: rgba(255, 255, 255, 0.12); border-color: rgba(255, 255, 255, 0.2); color: rgba(232, 238, 246, 0.95); }

        .region-arrow { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 50%; background: rgba(13, 21, 32, 0.72); border: 1px solid var(--glass-border); color: #F1F5F9; cursor: pointer; backdrop-filter: blur(8px); transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease; }
        .region-arrow:hover { background: rgba(13, 21, 32, 0.92); border-color: color-mix(in srgb, var(--gold) 55%, transparent); transform: scale(1.06); }
        .region-arrow:active { transform: scale(0.96); }

        .region-dots { display: flex; justify-content: center; align-items: center; gap: 0.45rem; margin-bottom: 1.3rem; }
        .region-dot { width: 8px; height: 8px; padding: 0; border-radius: 999px; border: none; background: rgba(226, 232, 240, 0.32); cursor: pointer; transition: background 0.2s ease, width 0.2s ease; }
        .region-dot:hover { background: rgba(226, 232, 240, 0.6); }
        .region-dot.active { width: 22px; background: var(--gold); }

        .menu-setup { display: flex; flex-direction: column; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .menu-clubname { width: min(320px, 80vw); padding: 0.6rem 0.9rem; background: rgba(13, 21, 32, 0.7); border: 1px solid var(--glass-border); border-radius: var(--radius-md); color: var(--text-primary); font-family: var(--font-body); font-size: 0.95rem; text-align: center; }
        .menu-clubname:focus { outline: none; border-color: color-mix(in srgb, var(--gold) 55%, transparent); }
        .menu-clubname::placeholder { color: var(--text-muted); }
        .menu-difficulty { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .menu-difficulty-label { font-family: var(--font-titles); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(226, 232, 240, 0.7); }
        .menu-difficulty-options { display: flex; gap: 0.5rem; }
        .menu-summary { font-size: 0.78rem; color: var(--gold); font-weight: 600; letter-spacing: 0.02em; }

        .menu-buttons { display: flex; flex-direction: column; gap: 0.7rem; width: 100%; max-width: 300px; margin: 0 auto 1.4rem; }
        .menu-step-nav { display: flex; gap: 0.6rem; width: 100%; max-width: 360px; margin: 0 auto 1.4rem; justify-content: center; }
        .menu-step-nav .menu-btn-main { flex: 1; }
        .menu-btn-back { flex: 0 0 auto; padding-left: 1rem; padding-right: 1.1rem; }
        .menu-btn-main { padding: 0.9rem 1.6rem; font-size: 1.02rem; font-family: var(--font-titles); font-weight: 700; letter-spacing: 0.03em; display: flex; align-items: center; justify-content: center; gap: 0.5rem; box-shadow: 0 6px 24px rgba(251, 146, 60, 0.35); }
        .menu-btn-ghost { display: flex; align-items: center; justify-content: center; gap: 0.5rem; background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.2); color: #F1F5F9; backdrop-filter: blur(8px); }
        .menu-btn-ghost:hover { background: rgba(255, 255, 255, 0.14); }
        .menu-version { color: rgba(148, 163, 184, 0.55); font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; }

        @media (max-width: 560px) {
          .menu-modes { grid-template-columns: 1fr; }
          .region-stage { height: 360px; }
          .region-arrow { width: 36px; height: 36px; }
          .region-carousel { gap: 0.3rem; }
          .logo-image { max-width: 180px; }
          .menu-buttons, .menu-step-nav { max-width: 100%; }
        }
      `}</style>
    </div>
  )
}
